import React, { useRef, useEffect } from 'react';

import {
  TextField,
  MenuItem,
  FormControl,
  FormHelperText,
} from '@material-ui/core';
import { useField } from '@unform/core';
import { isEqual } from 'lodash';
import PropTypes from 'prop-types';

import { canURL } from '~/helpers';

function Select({
  name,
  options,
  label,
  getValue,
  disabled,
  isGrid,
  initialValue,
  onValueChange,
  ...rest
}) {
  const inputRef = useRef(null);
  const permission = isEqual(canURL(), { read: true });

  const {
    fieldName,
    defaultValue,
    registerField,
    error,
    clearError,
  } = useField(name);
  const selectedDefaultValue =
    initialValue === null ? defaultValue : initialValue;

  function handleChangeValue(event) {
    const { value } = event.target;
    inputRef.current.value = value;

    getValue(value);
    onValueChange(value);
  }

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: inputRef.current,
      path: 'value',
    });
  }, [registerField, fieldName]);

  useEffect(() => {
    inputRef.current.value = selectedDefaultValue;
    getValue(selectedDefaultValue);
  }, [selectedDefaultValue]);

  useEffect(() => {
    if (disabled) clearError();
  }, [disabled]);

  return (
    <FormControl style={{ width: '100%' }} className="field-select">
      <TextField
        ref={inputRef}
        onChange={handleChangeValue}
        error={!!error}
        select
        label={label}
        variant="outlined"
        fullWidth
        size="small"
        InputLabelProps={{
          shrink: true,
        }}
        {...rest}
        defaultValue={selectedDefaultValue}
        disabled={(permission || disabled) && !isGrid}
        onFocus={clearError}
      >
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>
      {error && <FormHelperText error>{error}</FormHelperText>}
    </FormControl>
  );
}

Select.propTypes = {
  name: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.object).isRequired,
  label: PropTypes.string.isRequired,
  getValue: PropTypes.func,
  disabled: PropTypes.bool,
  isGrid: PropTypes.bool,
  initialValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onValueChange: PropTypes.func,
};

Select.defaultProps = {
  getValue: () => {},
  disabled: false,
  isGrid: false,
  initialValue: null,
  onValueChange: () => {},
};

export default Select;
